import { isIdentitySignalType, type IdentitySignalType } from '@aysar/contracts';
import { type TFunction } from 'i18next';

/**
 * A signal type's own name, in the operator's language.
 *
 * **It takes a `string`, not an `IdentitySignalType`, and that is the point.**
 * The registry is extensible: a deployment can run an api that knows a signal
 * this build does not, and every surface here — the queue's rows, its filter,
 * the policy editor — would otherwise print a raw `retina_hash` at an operator
 * or, worse, resolve `t('signal.retina_hash')` to the key itself. A type this
 * build has no word for is shown as the type, plainly, through a key that says
 * so out loud.
 */
export function signalLabel(t: TFunction, type: string): string {
  return isIdentitySignalType(type) ? t(`signal.${type}`) : t('signal.unknown', { type });
}

/**
 * The one-line explanation of what a signal actually is.
 *
 * `null` for a type this build does not know — a *guess* at what an unknown
 * signal means is worse than saying nothing, because an operator setting a
 * policy would be reading an explanation the platform invented.
 */
export function signalHint(t: TFunction, type: string): string | null {
  return isIdentitySignalType(type) ? t(`signalHint.${type}`) : null;
}

/** Narrows for the callers that need the descriptor, without repeating the guard. */
export function asSignalType(type: string): IdentitySignalType | null {
  return isIdentitySignalType(type) ? type : null;
}
